import { EventType } from '@app/shared/constants-server';
import type { RentalSaleCancellation } from '@app/shared/_models/sales';
import { dayNoonIso } from '@app/shared/time';
import type { Day, Instant } from './rental-billing.model';

// Corps des évènements 2102, 2103, 2105 et 2106 : le serveur refuse toute clé inconnue ou sans objet pour l'op (deny_unknown_fields), d'où ces fabriques seules.
// Les écrans passent des jours « AAAA-MM-JJ » ; ces fabriques, seules à écrire les clés start / end / day / prescribed_on, les envoient en instants
// au décalage de Paris, chaque jour à MIDI (dayNoonIso), qu'il ouvre ou ferme une plage.
// Toute plage est start / end, premier et dernier jour INCLUS (règle de JP du 27/10/2026). Le geste commercial (2104) n'existe plus : c'est une interruption au motif offert.
interface Target { order_id: number; line_id: number; }
interface Cancel { op: 'cancel'; id: number; cancel_reason: string; }
// Fin d'une prescription saisie à l'écran : un jour (end, inclus) ou une durée que le serveur convertit en end.
export type PrescriptionEnd = { end: Day; duration?: never } | { duration: { value: number; unit: 'DAY' | 'WEEK' | 'MONTH' }; end?: never };
type SentEnd = { end: Instant } | { duration: { value: number; unit: 'DAY' | 'WEEK' | 'MONTH' } };

// Interruption : start = premier jour non facturé (null = dès le premier jour de la fenêtre) ; end = dernier jour non facturé, inclus (null = ouverte).
interface InterruptionRange { start: Instant | null; end: Instant | null; reason_id: number; comment?: string }
export type InterruptionData = Target & (({ op: 'add' } & InterruptionRange) | Cancel | ({ op: 'replace'; id: number; cancel_reason: string } & InterruptionRange));
// Fenêtre : START day = premier jour facturé ; END day = DERNIER jour facturé, inclus (avant le 27/10/2026 : son lendemain) ; tous deux à midi.
export type WindowData = Target & ({ op: 'add'; bound: 'START' | 'END'; day: Instant; reason_id: number; comment?: string } | Cancel);
export type PrescriptionData = Target & ((({ op: 'add' } | { op: 'replace'; id: number; cancel_reason: string }) & { start: Instant; prescribed_on?: Instant; reference?: string } & SentEnd) | Cancel);
// seen = entrées de data.rental_sale_cancellations vues à l'écran : le serveur refuse si la ligne a changé depuis (double envoi, autre poste).
export type SaleCancellationData = Target & ({ op: 'add'; qty: number; reason_id: number; comment?: string; seen: number } | Cancel);

export type RentalDecisionEvent =
  | { typ: EventType.RENTAL_INTERRUPTION; data: InterruptionData }
  | { typ: EventType.RENTAL_WINDOW; data: WindowData }
  | { typ: EventType.RENTAL_PRESCRIPTION; data: PrescriptionData }
  | { typ: EventType.RENTAL_SALE_CANCELLATION; data: SaleCancellationData };
export type RentalDecisionTyp = RentalDecisionEvent['typ'];

// Réponse d'une 2106 acceptée ; 2102, 2103, 2105 : RentalDecisionResult (quotations.service.ts), inchangé.
export interface RentalSaleDecisionResult { order_id: number; line_id: number; decision_id: number; rental_sale_cancellations: RentalSaleCancellation[]; }

function trimmed(s: string | null | undefined): string {
  return (s ?? '').trim();
}

// Clé facultative : absente plutôt que vide.
function opt<K extends string>(key: K, s: string | null | undefined): { [P in K]?: string } {
  const v = trimmed(s);
  return (v ? { [key]: v } : {}) as { [P in K]?: string };
}

// Jour => midi de Paris (shared/time.ts). Une valeur non convertible part telle quelle : le serveur répond 400.
function noonAt(day: Day): Instant {
  return dayNoonIso(day) ?? day;
}

function endOf(end: PrescriptionEnd): SentEnd {
  return end.duration ? { duration: { value: end.duration.value, unit: end.duration.unit } } : { end: noonAt(end.end as Day) };
}

// Jour facultatif : clé absente plutôt que vide.
function optDay<K extends string>(key: K, day: Day | null | undefined): { [P in K]?: Instant } {
  return (day ? { [key]: noonAt(day) } : {}) as { [P in K]?: Instant };
}

// v.end = dernier jour non facturé, inclus (null = ouverte).
export function interruptionAdd(orderId: number, lineId: number, v: { start: Day | null; end: Day | null; reason_id: number; comment?: string | null }): RentalDecisionEvent {
  return {
    typ: EventType.RENTAL_INTERRUPTION,
    data: {
      order_id: orderId, line_id: lineId, op: 'add', start: v.start == null ? null : noonAt(v.start), end: v.end == null ? null : noonAt(v.end),
      reason_id: v.reason_id, ...opt('comment', v.comment),
    },
  };
}

export function interruptionReplace(orderId: number, lineId: number, id: number, cancelReason: string, v: { start: Day | null; end: Day | null; reason_id: number; comment?: string | null }): RentalDecisionEvent {
  return {
    typ: EventType.RENTAL_INTERRUPTION,
    data: {
      order_id: orderId, line_id: lineId, op: 'replace', id, cancel_reason: trimmed(cancelReason),
      start: v.start == null ? null : noonAt(v.start), end: v.end == null ? null : noonAt(v.end), reason_id: v.reason_id, ...opt('comment', v.comment),
    },
  };
}

// day : START = premier jour facturé ; END = dernier jour facturé, inclus ; envoyé à midi dans les deux cas.
export function windowAdd(orderId: number, lineId: number, bound: 'START' | 'END', day: Day, reasonId: number, comment?: string | null): RentalDecisionEvent {
  return { typ: EventType.RENTAL_WINDOW, data: { order_id: orderId, line_id: lineId, op: 'add', bound, day: noonAt(day), reason_id: reasonId, ...opt('comment', comment) } };
}

export function prescriptionAdd(orderId: number, lineId: number, v: { start: Day; end: PrescriptionEnd; prescribed_on?: Day | null; reference?: string | null }): RentalDecisionEvent {
  return {
    typ: EventType.RENTAL_PRESCRIPTION,
    data: { order_id: orderId, line_id: lineId, op: 'add', start: noonAt(v.start), ...endOf(v.end), ...optDay('prescribed_on', v.prescribed_on), ...opt('reference', v.reference) },
  };
}

export function prescriptionReplace(orderId: number, lineId: number, id: number, cancelReason: string, v: { start: Day; end: PrescriptionEnd; prescribed_on?: Day | null; reference?: string | null }): RentalDecisionEvent {
  return {
    typ: EventType.RENTAL_PRESCRIPTION,
    data: {
      order_id: orderId, line_id: lineId, op: 'replace', id, cancel_reason: trimmed(cancelReason),
      start: noonAt(v.start), ...endOf(v.end), ...optDay('prescribed_on', v.prescribed_on), ...opt('reference', v.reference),
    },
  };
}

export function saleCancellationAdd(orderId: number, lineId: number, qty: number, reasonId: number, comment: string | null | undefined, seen: number): RentalDecisionEvent {
  return { typ: EventType.RENTAL_SALE_CANCELLATION, data: { order_id: orderId, line_id: lineId, op: 'add', qty: cleanQty(qty), reason_id: reasonId, ...opt('comment', comment), seen } };
}

export function decisionCancel(typ: RentalDecisionTyp, orderId: number, lineId: number, id: number, cancelReason: string): RentalDecisionEvent {
  return { typ, data: { order_id: orderId, line_id: lineId, op: 'cancel', id, cancel_reason: trimmed(cancelReason) } } as RentalDecisionEvent;
}

export function decisionTarget(e: RentalDecisionEvent): { lineId: number; op: string } {
  return { lineId: e.data.line_id, op: e.data.op };
}

// Bruit flottant seulement (0,30000000000000004 → 0,3) ; une saisie à 3 décimales part telle quelle et le serveur la refuse avec son message.
export function cleanQty(q: number): number {
  return Number.isFinite(q) ? Number.parseFloat(q.toPrecision(12)) : q;
}
